import ClickAwayListener from "@mui/base/ClickAwayListener";
import Color from "color";
import { ReactNode, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";

import { logout } from "../../services/auth";
import { chooseBestContrast } from "../../utils/colorContrast";
import { IconButton } from "../button";
import { BackSvg } from "../icons";
import * as Style from "./ScreenHeader.style";
import useCurrentUser from "../../hooks/useCurrentUser";
import useCompetition from "../../hooks/useCompetition";

const getVariantColor = (color: string) => {
  const c = Color(color);
  if (c.luminosity() > 0.8) {
    return c.darken(0.1).toString();
  } else {
    return c.lighten(0.25).toString();
  }
};

export default function ScreenHeader({
  children,
  canGoBack = true,
  color = "#FFFFFF",
  rightMenuItems = [],
}: {
  children: ReactNode;
  canGoBack?: boolean;
  color?: string;
  rightMenuItems?: { text: string; onClick: () => void }[];
}) {
  const [firstColor, ...otherColors] = color.split(":");
  const { profile } = useCurrentUser();

  if (!firstColor) {
    throw new Error("Invalid color");
  }
  const navigate = useNavigate();

  const variantColor = getVariantColor(firstColor);
  const foregroundColor = chooseBestContrast(firstColor, [
    "#FFFFFF",
    "#000121",
  ]);

  const [isMenuVisible, setIsMenuVisible] = useState(false);

  return (
    <Style.Header
      backgroundColor={firstColor}
      color={foregroundColor}
      otherColors={otherColors}
    >
      <Helmet>
        <meta name="theme-color" content={variantColor} />
      </Helmet>
      <Style.HeaderLeft>
        {canGoBack ? (
          <IconButton
            onClick={() => navigate(-1)}
            style={{
              backgroundColor: variantColor,
              color: foregroundColor,
            }}
          >
            <BackSvg />
          </IconButton>
        ) : null}
      </Style.HeaderLeft>
      <Style.HeaderText>{children}</Style.HeaderText>
      <Style.HeaderRight>
        <ClickAwayListener onClickAway={() => setIsMenuVisible(false)}>
          <>
            <IconButton
              onClick={() => setIsMenuVisible(!isMenuVisible)}
              style={{
                backgroundColor: variantColor,
                color: foregroundColor,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.48317 15.4336L5.18317 13.1168C5.18317 13.0832 5.16104 13.0555 5.11677 13.0336C5.07197 13.0112 5.03864 13 5.01677 13L2.83357 13.9168L0.316772 9.48322L2.13357 8.10002C2.14477 8.08882 2.15304 8.06935 2.15837 8.04162C2.16371 8.01388 2.16637 7.99442 2.16637 7.98322C2.16637 7.97202 2.16371 7.95548 2.15837 7.93362C2.15304 7.91122 2.14477 7.89442 2.13357 7.88322L0.316772 6.50001L2.83357 2.11681L5.01677 3.00002C5.04984 3.01122 5.08584 3.00562 5.12477 2.98322C5.16371 2.96135 5.18877 2.93362 5.19997 2.90002L5.48317 0.550415H10.5168L10.8 2.90002C10.8112 2.93362 10.8418 2.96135 10.892 2.98322C10.9416 3.00562 10.9832 3.01681 11.0168 3.01681L13.1664 2.11681L15.6832 6.50001L13.8496 7.88322C13.8389 7.89442 13.8336 7.91388 13.8336 7.94162C13.8336 7.96935 13.8336 7.98882 13.8336 8.00002C13.8336 8.01122 13.8306 8.03068 13.8248 8.05842C13.8194 8.08615 13.8277 8.10562 13.8496 8.11682L15.6832 9.48322L13.1336 13.9168L10.9504 13C10.928 12.9888 10.9 12.9971 10.8664 13.0248C10.8333 13.0525 10.8168 13.0776 10.8168 13.1L10.5168 15.4336H5.48317ZM7.93357 10.5504C8.64451 10.5504 9.2501 10.3003 9.75037 9.80002C10.2501 9.29975 10.5 8.69415 10.5 7.98322C10.5 7.28348 10.2501 6.68081 9.75037 6.17521C9.2501 5.66961 8.64451 5.41681 7.93357 5.41681C7.23331 5.41681 6.63037 5.66961 6.12477 6.17521C5.61917 6.68081 5.36637 7.28348 5.36637 7.98322C5.36637 8.69415 5.61917 9.29975 6.12477 9.80002C6.63037 10.3003 7.23331 10.5504 7.93357 10.5504Z"
                  fill="currentColor"
                />
              </svg>
            </IconButton>
            <Style.RightMenu isVisible={isMenuVisible}>
              {profile ? (
                <>
                  <Style.RightMenuItem>
                    <span className="text-muted">
                      Logged in as {profile.name}.
                    </span>
                  </Style.RightMenuItem>
                </>
              ) : null}
              {rightMenuItems?.map((item, index) => (
                <Style.RightMenuItem
                  key={index}
                  onClick={() => {
                    setIsMenuVisible(false);
                    item.onClick();
                  }}
                >
                  {item.text}
                </Style.RightMenuItem>
              ))}
              {profile ? (
                <Style.RightMenuItem
                  onClick={() => logout("https://griptonite.io")}
                >
                  Log out
                </Style.RightMenuItem>
              ) : null}
            </Style.RightMenu>
          </>
        </ClickAwayListener>
      </Style.HeaderRight>
    </Style.Header>
    // <div
    //   className="d-flex align-items-center bg-dark text-light"
    //   style={{ minHeight: 40, marginRight: -12, marginLeft: -12 }}
    // >
    //   <div style={{ width: 100 }}>
    //     {canGoBack ? (
    //       <Button
    //         variant="secondary-outline"
    //         as="div"
    //         className="text-light"
    //         onClick={() => navigate(-1)}
    //       >

    //       </Button>
    //     ) : (
    //       <Button
    //         variant="secondary-outline"
    //         as="div"
    //         className="text-light"
    //         onClick={onLogout}
    //       >
    //         <svg
    //           xmlns="http://www.w3.org/2000/svg"
    //           width="20"
    //           height="20"
    //           fill="currentColor"
    //           className="bi bi-arrow-left d-inline-block me-1"
    //           viewBox="0 0 16 16"
    //           style={{ marginTop: -3 }}
    //         >
    //           <path
    //             fillRule="evenodd"
    //             d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"
    //           />
    //         </svg>
    //         Logout
    //       </Button>
    //     )}
    //   </div>
    //   <div className="flex-fill fw-bolder text-center">{children}</div>
    //   <div style={{ width: 100 }} />
    // </div>
  );
}
